import { computed, ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';

/**
 * Onde cada módulo da conversa mora: a mecânica do "fixar no topo".
 *
 * Três casas, e o módulo vive em UMA só:
 *   topo  → pílula expandida no cabeçalho, sempre à mostra
 *   barra → ícone no trilho da direita (o padrão)
 *   menu  → só dentro do ⋮
 *
 * A preferência é de quem atende, não da conta: cada um monta o próprio
 * cabeçalho. Guardada em uiSettings, a mesma mecânica que o cabeçalho já usa
 * para os itens ocultos de hoje.
 *
 * O teto de dois fixados não é capricho: é o que cabe ao lado do nome e do
 * Resolver sem o cabeçalho virar de novo o entulho que a V3 acabou de matar.
 */
export const CHAVE = 'conversation_header_pinned_items';
export const CHAVE_LEGADA = 'conversation_header_hidden_items';
// A ordem dos fixados no topo, de quem atende. Antes era a ordem de fixação e
// mudar exigia desfixar; o Lucas derrubou isso em 01/09: arrasta e pronto.
export const CHAVE_ORDEM = 'conversation_header_pinned_order';

export const CASAS = Object.freeze({
  TOPO: 'topo',
  BARRA: 'barra',
  MENU: 'menu',
});

export const MODULOS = Object.freeze([
  'ai',
  'agent',
  'funnel',
  'tasks',
  'agenda',
]);

// Sem teto (Lucas, 01/09/2026): fixa quantos quiser. Quem decide o que cabe
// e o cabecalho, medindo o espaco real e despromovendo o resto para o trilho.

const CASAS_VALIDAS = Object.values(CASAS);

// Tela estreita despromove o segundo fixado para a barra. É estado de LAYOUT,
// não de preferência: vive aqui (compartilhado) e não em uiSettings, para o
// trilho mostrar o módulo sem ninguém regravar a escolha da pessoa. Sem isto o
// despromovido ficava órfão: fora do topo e fora do trilho (QA da v968).
const despromovidos = ref([]);

export function useHeaderPins() {
  const { uiSettings, updateUISettings } = useUISettings();
  const motivoRecusa = ref('');

  // Lê o que está salvo e joga fora o que não faz sentido: valor desconhecido
  // ou módulo que não existe mais viram barra, sem quebrar a tela de ninguém.
  const casas = computed(() => {
    const salvo = uiSettings.value?.[CHAVE];
    const mapa = {};

    // Quem já escondia um módulo continua sem ele: item oculto legado é a
    // casa "menu" com outro nome. Migração de mão única, na leitura.
    const ocultosLegados = uiSettings.value?.[CHAVE_LEGADA];
    if (Array.isArray(ocultosLegados)) {
      ocultosLegados.forEach(nome => {
        if (MODULOS.includes(nome)) mapa[nome] = CASAS.MENU;
      });
    }

    if (salvo && typeof salvo === 'object') {
      Object.entries(salvo).forEach(([nome, casa]) => {
        if (MODULOS.includes(nome) && CASAS_VALIDAS.includes(casa)) {
          mapa[nome] = casa;
        }
      });
    }
    return mapa;
  });

  const casaDe = modulo => casas.value[modulo] || CASAS.BARRA;

  // Ordem: a que a pessoa arrastou (salva), e quem entrou depois vai para o
  // fim. Lixo salvo (módulo que não está mais no topo) é ignorado na leitura.
  const noTopo = computed(() =>
    Object.entries(casas.value)
      .filter(([, casa]) => casa === CASAS.TOPO)
      .map(([nome]) => nome)
  );
  const fixados = computed(() => {
    const salva = uiSettings.value?.[CHAVE_ORDEM];
    const ordenados = Array.isArray(salva)
      ? salva.filter(
          (m, i) => noTopo.value.includes(m) && salva.indexOf(m) === i
        )
      : [];
    return [...ordenados, ...noTopo.value.filter(m => !ordenados.includes(m))];
  });

  // Reordenar só rearruma quem já está no topo; não fixa nem devolve ninguém.
  const reordenar = lista => {
    const nova = lista.filter(
      (m, i) => noTopo.value.includes(m) && lista.indexOf(m) === i
    );
    if (nova.length !== noTopo.value.length) return false;
    updateUISettings({ [CHAVE_ORDEM]: nova });
    return true;
  };

  const podeFixar = computed(() => true);

  const gravar = (modulo, casa) => {
    const atual = { ...(uiSettings.value?.[CHAVE] || {}) };
    if (casa === CASAS.BARRA) {
      delete atual[modulo];
    } else {
      atual[modulo] = casa;
    }
    updateUISettings({ [CHAVE]: atual });
  };

  const fixar = modulo => {
    motivoRecusa.value = '';
    if (casaDe(modulo) === CASAS.TOPO) return true;
    gravar(modulo, CASAS.TOPO);
    return true;
  };

  const devolver = modulo => {
    motivoRecusa.value = '';
    gravar(modulo, CASAS.BARRA);
  };

  const paraMenu = modulo => {
    motivoRecusa.value = '';
    gravar(modulo, CASAS.MENU);
  };

  const moverPara = (modulo, casa) => {
    if (casa === CASAS.TOPO) return fixar(modulo);
    if (casa === CASAS.MENU) paraMenu(modulo);
    else devolver(modulo);
    return true;
  };

  const naBarra = computed(() =>
    MODULOS.filter(
      modulo =>
        casaDe(modulo) === CASAS.BARRA || despromovidos.value.includes(modulo)
    )
  );

  const despromover = lista => {
    despromovidos.value = lista.filter(m => casaDe(m) === CASAS.TOPO);
  };

  return {
    casaDe,
    fixados,
    reordenar,
    naBarra,
    despromovidos,
    despromover,
    podeFixar,
    motivoRecusa,
    fixar,
    devolver,
    paraMenu,
    moverPara,
  };
}
